import React, { useEffect, useMemo, useState } from 'react'
import SummaryIndicator from './SummaryIndicator'
import { isUndefined, last, includes, round } from 'lodash'
import { NoDataPanel } from 'components/card/NoDataCard'
import { iaeauResultsShape, linkStationDetailsShape } from 'territory/shapes/A3PShapes'
import EChartV6 from 'components/echart/EChartV6'
import { Grid } from '@mui/material'
import PropTypes from 'prop-types'
import { VP_LEVELS, VP_LEVELS_PARAMETERS } from 'territory/constants/A3PConstants'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { hasValue } from 'utils/NumberUtil'
import { errorColor, green, grey, lightErrorColor, lightWarningColor, veryLightGreen, warningColor } from 'utils/constants/ColorTheme'
import { getDarkerHex } from 'utils/ColorUtil'
import { formatMilliers } from 'utils/StringUtil'
import { makePiecewiseMapper } from 'territory/utils/A3PUtils'
import { DEPTH } from 'utils/PiezometryUtils'
import { HYDRO_MEASURE_COTE, HYDROMETER_HEIGHT_TYPE } from 'hydrometry/constants/HydrometryConstants'
import { ChipLabel, getCote, getHydroCote } from 'components/echart/ChartOptionsChip'
import i18n from 'simple-react-i18n'
import { useSelector } from 'react-redux'
import { MEASURE_COTE } from 'piezometry/constants/PiezometryConstants'

const DATATYPES_WITH_NGF = [DEPTH, HYDROMETER_HEIGHT_TYPE]

const DEFAULT_VALUE = 1
const NGF_VALUE = 2

const RESSOURCE_BAR_COLOR = '#7191d4'
const RESSOURCE_BACKGROUND_COLOR = '#e9eff9'

const NO_COVERING_BACKGROUND_COLOR = '#eeeeee'

const chartHeight = 500

const ManagementNeedsChart = ({ iaeauResults, vpLevel, vpLevelValues = [], selectedStation }) => {
    const {
        hydrometryDataTypes,
        piezometryDataTypes,
    } = useSelector(store => ({
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
        piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
    }))

    const VPRestrictedLevelLabel = useApplicationSetting(VP_LEVELS_PARAMETERS[VP_LEVELS.RESTRICTED].label, v => hasValue(v) ? String(v) : null)
    const VPNormalLevelLabel = useApplicationSetting(VP_LEVELS_PARAMETERS[VP_LEVELS.NORMAL].label, v => hasValue(v) ? String(v) : null)
    const VPHighLevelLabel = useApplicationSetting(VP_LEVELS_PARAMETERS[VP_LEVELS.HIGH].label, v => hasValue(v) ? String(v) : null)
    const VPVeryHighLevelLabel = useApplicationSetting(VP_LEVELS_PARAMETERS[VP_LEVELS.VERY_HIGH].label, v => hasValue(v) ? String(v) : null)
    const [VPRestrictedLevelValue, VPNormalLevelValue, VPHighLevelValue, VPVeryHighLevelValue] = vpLevelValues
    const vpThresholds = [
        { name: VPRestrictedLevelLabel ?? i18n.VPRestricted, value: VPRestrictedLevelValue, key: VP_LEVELS.RESTRICTED },
        { name: VPNormalLevelLabel ?? i18n.VPNormal, value: VPNormalLevelValue, key: VP_LEVELS.NORMAL },
        { name: VPHighLevelLabel ?? i18n.VPHigh, value: VPHighLevelValue, key: VP_LEVELS.HIGH },
        { name: VPVeryHighLevelLabel ?? i18n.VPVeryHigh, value: VPVeryHighLevelValue, key: VP_LEVELS.VERY_HIGH },
    ]

    const isPiezo = useMemo(() => selectedStation?.dataType === DEPTH, [selectedStation])
    const dataTypes = useMemo(() => isPiezo ? piezometryDataTypes : hydrometryDataTypes, [isPiezo, piezometryDataTypes, hydrometryDataTypes])

    const [displayCote, setDisplayCote] = useState(isPiezo ? MEASURE_COTE.DEPTH : HYDRO_MEASURE_COTE.HEIGHT)
    const displayNGF = useMemo(() => displayCote === HYDRO_MEASURE_COTE.NGF && includes(DATATYPES_WITH_NGF, selectedStation?.dataType), [displayCote, selectedStation])

    useEffect(() => {
        setDisplayCote(isPiezo ? MEASURE_COTE.DEPTH : HYDRO_MEASURE_COTE.HEIGHT)
    }, [selectedStation, isPiezo])

    const getContent = () => {
        if (isUndefined(iaeauResults) || isUndefined(selectedStation)) return <NoDataPanel />
        const vpValue = vpThresholds.find(({ key }) => key === vpLevel)?.value
        const estimatedNeedsValue = iaeauResults.graphe_thermometre?.besoin_total_estime
        const coveringRate = hasValue(vpValue) && estimatedNeedsValue > 0 ? vpValue / estimatedNeedsValue : undefined
        const getCoveringColors = () => {
            if (!hasValue(coveringRate)) return { backgroundColor: NO_COVERING_BACKGROUND_COLOR, color: grey, darkerColor: getDarkerHex(grey, 20) }
            if (coveringRate >= 1) return { backgroundColor: veryLightGreen, color: green, darkerColor: getDarkerHex(green, 20) }
            if (coveringRate >= 0.8) return { backgroundColor: lightWarningColor, color: warningColor, darkerColor: getDarkerHex(warningColor, 20) }
            return { backgroundColor: lightErrorColor, color: errorColor, darkerColor: getDarkerHex(errorColor, 20) }
        }
        const { backgroundColor, color, darkerColor } = getCoveringColors()

        // --- Axe Y normalisé `u` : les 4 niveaux VP sont aux positions entières 0..3, communes
        // aux 2 grids. Côté ressource les 3 ancres dry* (à la date de lastMeasure) sont à u=0,1,2 ;
        // VP++ (u=3) est extrapolé. Côté besoin les 4 seuils VP sont à u=0,1,2,3.
        const targetDate = last(selectedStation.measures)?.[0]
        // L'enveloppe fournit une valeur tous les 7 jours : on ne cherche pas la date exacte de la
        // dernière mesure mais la date d'enveloppe la plus proche, inférieure ou égale à celle-ci.
        const dryValueAt = (arr) => {
            if (!arr?.length) return undefined
            const match = hasValue(targetDate)
                ? arr.reduce((closest, cur) => (
                    cur[0] <= targetDate && (!closest || cur[0] > closest[0]) ? cur : closest
                ), undefined)
                : undefined
            return (match ?? last(arr))?.[displayNGF ? NGF_VALUE : DEFAULT_VALUE]
        }
        const resQuinq = dryValueAt(selectedStation.dryQuinquennial)
        const resQuad = dryValueAt(selectedStation.dryQuadrennial)
        const resTri = dryValueAt(selectedStation.dryTriennial)
        // En profondeur (hors cote NGF) l'axe est inversé : plus c'est profond, pire c'est, donc les
        // seuils décroissent quand u croît (resQuinq > resQuad > resTri). La base u=0 ne peut alors pas
        // être la valeur 0 (= surface = meilleur état) : on l'extrapole côté "plus profond" que la
        // quinquennale pour garder une relation u<->valeur monotone (sinon le tri du mapper la casse).
        const isDepthAxis = isPiezo && !displayNGF
        const resourceBaseValue = isDepthAxis && hasValue(resQuinq) && hasValue(resQuad)
            ? resQuinq + (resQuinq - resQuad)
            : 0
        // u=0 = base des barres (valeur réelle 0, ou seuil extrapolé en profondeur) ; seuils VP à u = niveau+1
        const mapResource = makePiecewiseMapper([
            { u: 0, value: resourceBaseValue },
            { u: 1, value: resQuinq },
            { u: 2, value: resQuad },
            { u: 3, value: resTri },
        ])
        const mapNeed = makePiecewiseMapper([
            { u: 0, value: 0 },
            { u: 1, value: VPRestrictedLevelValue },
            { u: 2, value: VPNormalLevelValue },
            { u: 3, value: VPHighLevelValue },
            { u: 4, value: VPVeryHighLevelValue },
        ])

        const resourceU = mapResource?.(displayNGF ? selectedStation.NGFLastMeasure : selectedStation.lastMeasure)
        const estimatedNeedsU = mapNeed?.(estimatedNeedsValue)
        const vpU = mapNeed?.(vpValue) // barre VP autorisée -> u du seuil correspondant (= vpLevel+1)

        // Valeur réelle de chaque seuil VP, par côté (index = niveau VP = u).
        // Ressource : 3 ancres dry* connues à u=0,1,2 ; VP++ (u=3) extrapolé par le dernier segment.
        const resVeryHigh = hasValue(resTri) && hasValue(resQuad) ? resTri + (resTri - resQuad) : undefined
        const resourceValueByU = [resQuinq, resQuad, resTri, resVeryHigh]
        const needValueByU = [VPRestrictedLevelValue, VPNormalLevelValue, VPHighLevelValue, VPVeryHighLevelValue]

        // Libellés par côté : à gauche la classe de période de retour, à droite le nom du niveau VP
        const resourceClassByU = [`< ${i18n.fre_5}`, `< ${i18n.fre_4}`, `< ${i18n.fre_3}`, `> ${i18n.fre_3}`]
        const vpNameByU = [VP_LEVELS.RESTRICTED, VP_LEVELS.NORMAL, VP_LEVELS.HIGH, VP_LEVELS.VERY_HIGH]
            .map(k => vpThresholds.find(({ key }) => key === k)?.name || '')

        // Lignes historiques (besoin), valeurs réelles en m3 converties en `u`
        const historicalLines = [
            // { name: 'Max. Historique', value: 3000000 },
            // { name: 'Min. Historique', value: 1650000 },
            // { name: 'Vol. Demandé', value: 2600000 },
            // { name: 'Vol. Autorisé', value: 2300000 },
        ]

        // Bornes communes de l'axe `u` : on garde [0, 3] et on élargit si une donnée tracée déborde
        const plottedU = [resourceU, estimatedNeedsU, vpU, ...historicalLines.map(({ value }) => mapNeed?.(value))]
            .filter(hasValue)
        const uMin = 0 // le bas de la grille = u=0 = valeur réelle 0 (base des barres)
        const uMax = Math.max(4, ...plottedU) + 0.4 // seuil max à u=4 ; marge au-dessus pour les labels

        // Les 4 seuils VP : lignes horizontales à u = 0..3, alignées sur les 2 grids car l'axe `u`
        // y est identique. Étiquetées avec la vraie valeur du côté concerné ; le VP courant est mis en avant.
        const VP_ORDER = [VP_LEVELS.RESTRICTED, VP_LEVELS.NORMAL, VP_LEVELS.HIGH, VP_LEVELS.VERY_HIGH]
        const vpLineStyle = (k) => {
            const isCurrent = k === vpLevel
            return {
                color: isCurrent ? darkerColor : '#ccc',
                width: isCurrent ? 2.5 : 1,
                type: isCurrent ? 'solid' : 'dashed',
            }
        }
        // markLines des seuils, dessinées au-dessus (z élevé).
        // - `span` = [xDébut, xFin] en % du conteneur : trace une ligne étendue traversant les 2 grids
        //   (la série doit avoir clip: false). Sans `span`, ligne pleine largeur du grid.
        // - `hideLine` : ne garde que l'étiquette (trait masqué), pour le 2e libellé sur la même hauteur.
        const buildVpThresholdLines = ({ valuesByU, namesByU, position, formatValue, span, hideLine }) => ({
            silent: true,
            symbol: ['none', 'none'],
            z: 10,
            data: VP_ORDER.map((k) => {
                const u = k + 1 // seuils décalés d'un cran (u=0 réservé à la valeur réelle 0)
                const isCurrent = k === vpLevel
                const realValue = valuesByU[k]
                const name = namesByU[k] || ''
                const lineStyle = hideLine ? { color: 'transparent' } : vpLineStyle(k)
                const label = {
                    show: true,
                    position,
                    color: isCurrent ? darkerColor : '#666',
                    fontWeight: isCurrent ? 'bold' : 'normal',
                    backgroundColor: '#fff',
                    borderColor: isCurrent ? darkerColor : '#ccc',
                    borderWidth: isCurrent ? 1.5 : 1,
                    borderRadius: 12,
                    padding: [4, 8],
                    formatter: () => hasValue(realValue) ? `${name}\n${formatValue(realValue)}` : name,
                }
                return span
                    ? [{ x: span[0], yAxis: u, lineStyle, label }, { x: span[1], yAxis: u }]
                    : { yAxis: u, lineStyle, label }
            }),
        })

        const baseGrid = {
            show: true,
            top: 50,
            bottom: 20,
            width: '40%',
            borderColor: 'transparent',
        }
        const grids = [{
            ...baseGrid,
            id: i18n.waterResources,
            left: '5%',
            backgroundColor: RESSOURCE_BACKGROUND_COLOR,
        }, {
            ...baseGrid,
            id: i18n.projectedWaterDemandAtEndOfYear,
            right: '5%',
            backgroundColor,
        }]

        const xAxis = grids.map(({ id }, idx) => ({
            gridIndex: idx,
            name: id,
            type: 'category',
            position: 'top',
            nameGap: 35,
            nameLocation: 'center',
            splitLine: {
                show: false,
            },
            axisLabel: {
                show: false,
            },
        }))

        // Axe `u` caché, mêmes bornes des 2 côtés => un même `u` tombe au même pixel sur les 2 grids
        const yAxis = grids.map((_, idx) => ({
            gridIndex: idx,
            type: 'value',
            min: uMin,
            max: uMax,
            splitLine: {
                show: false,
            },
            axisLabel: {
                show: false,
            },
        }))

        const seriesLabelStyle = {
            show: true,
            position: 'insideTop',
            padding: [4, 8],
            borderRadius: 8,
            color: 'black',
            borderColor: '#000',
            borderWidth: '1',
        }

        // --- Placement adaptatif des labels de barres pour éviter qu'ils soient cachés/superposés ---
        const uRange = uMax - uMin
        const SHALLOW_U = uRange * 0.10 // barre considérée "trop basse" (label ne tient pas en insideTop)
        const CLOSE_U = uRange * 0.10 // deux niveaux "très proches" (labels insideTop se chevaucheraient)
        const isShallow = (u) => hasValue(u) && (u - uMin) < SHALLOW_U

        // Gauche (1 barre) : insideTop par défaut, au-dessus (top) si trop basse
        const resourceLabelPos = isShallow(resourceU) ? 'top' : 'insideTop'

        // Droite (2 barres superposées : besoin et VP autorisé) : positions choisies pour éviter
        // qu'un label soit caché ou que les deux se chevauchent.
        const needLow = isShallow(estimatedNeedsU)
        const vpLow = isShallow(vpU)
        const levelsClose = hasValue(estimatedNeedsU) && hasValue(vpU) && Math.abs(estimatedNeedsU - vpU) < CLOSE_U
        const getNeedSideLabelPositions = () => {
            // les 2 trop basses -> à l'extérieur, l'un à gauche l'autre à droite
            if (needLow && vpLow) return { needLabelPos: 'left', vpLabelPos: 'right' }
            // une seule trop basse -> elle passe au-dessus (même logique que la barre de gauche)
            if (needLow) return { needLabelPos: 'top', vpLabelPos: 'insideTop' }
            if (vpLow) return { needLabelPos: 'insideTop', vpLabelPos: 'top' }
            // assez hautes mais très proches -> la barre du dessus passe au-dessus
            if (levelsClose && estimatedNeedsU >= vpU) return { needLabelPos: 'top', vpLabelPos: 'insideTop' }
            if (levelsClose) return { needLabelPos: 'insideTop', vpLabelPos: 'top' }
            return { needLabelPos: 'insideTop', vpLabelPos: 'insideTop' }
        }
        const { needLabelPos, vpLabelPos } = getNeedSideLabelPositions()

        const series = [
            {
                xAxisIndex: 0,
                yAxisIndex: 0,
                data: [{ value: resourceU, real: displayNGF ? round(selectedStation?.NGFLastMeasure, 3) : round(selectedStation?.lastMeasure, 3) }],
                type: 'bar',
                barMaxWidth: 200,
                color: RESSOURCE_BAR_COLOR,
                itemStyle: {
                    borderRadius: [8, 8, 0, 0],
                },
                label: {
                    ...seriesLabelStyle,
                    position: resourceLabelPos,
                    backgroundColor: RESSOURCE_BACKGROUND_COLOR,
                    formatter: ({ data }) => (hasValue(data.real) ? `${data.real} ${dataTypes.find(({ id }) => id === selectedStation.dataType)?.unit || i18n.metersSymbol}` : '-'),
                },
                markLine: buildVpThresholdLines({
                    valuesByU: resourceValueByU,
                    namesByU: resourceClassByU,
                    position: 'insideStart',
                    formatValue: v => `${Number(round(v, 2))} ${dataTypes.find(({ id }) => id === selectedStation.dataType)?.unit || i18n.metersSymbol}`,
                    span: ['5%', '95%'],
                }),
            },
            {
                xAxisIndex: 1,
                yAxisIndex: 1,
                data: [{ value: estimatedNeedsU, real: estimatedNeedsValue }],
                type: 'bar',
                barMaxWidth: 200,
                color,
                itemStyle: {
                    borderRadius: [8, 8, 0, 0],
                },
                label: {
                    ...seriesLabelStyle,
                    position: needLabelPos,
                    backgroundColor,
                    formatter: ({ data }) => (hasValue(data.real) ? `${formatMilliers(data.real)} m3` : '-'),
                },
                markLine: mapNeed ? {
                    silent: true,
                    symbol: ['none', 'none'],
                    z: 0,
                    itemStyle: {
                        color: '#ccc',
                    },
                    label: {
                        position: 'end',
                        formatter: ({ data }) =>`${data.name} :\n${formatMilliers(data.real)} m3`,
                    },
                    data: historicalLines.map(({ name, value }) => {
                        const u = mapNeed(value)
                        return [
                            { name, real: value, yAxis: u, x: '55%' },
                            { name, real: value, yAxis: u, x: '85%' },
                        ]
                    }),
                } : undefined,
            },
            {
                xAxisIndex: 1,
                yAxisIndex: 1,
                data: [{ value: vpU, real: vpValue }],
                type: 'bar',
                barGap: '-100%',
                barMaxWidth: 200,
                color: darkerColor,
                z: coveringRate > 1 ? 1 : 3,
                itemStyle: {
                    borderRadius: [8, 8, 0, 0],
                },
                label: {
                    ...seriesLabelStyle,
                    position: vpLabelPos,
                    backgroundColor,
                    formatter: ({ data }) => (hasValue(data.real) ? `${formatMilliers(data.real)} m3` : '-'),
                },
                markLine: buildVpThresholdLines({
                    valuesByU: needValueByU,
                    namesByU: vpNameByU,
                    position: 'start',
                    formatValue: v => `${formatMilliers(v)} m3`,
                    hideLine: true,
                }),
            },
        ]

        return (
            <Grid container size={12} spacing={1}>
                {includes(DATATYPES_WITH_NGF, selectedStation?.dataType) && <ChipLabel
                    label={isPiezo ? getCote(displayCote).label : getHydroCote(displayCote).label}
                    onClick={() => setDisplayCote(prev => {
                        if (isPiezo) return prev === MEASURE_COTE.DEPTH ? MEASURE_COTE.NGF : MEASURE_COTE.DEPTH
                        return prev === HYDRO_MEASURE_COTE.HEIGHT ? HYDRO_MEASURE_COTE.NGF : HYDRO_MEASURE_COTE.HEIGHT
                    })}
                />}
                <Grid size={12}>
                    <EChartV6
                        option={{
                            grid: grids,
                            xAxis,
                            yAxis,
                            series,
                        }}
                        withToolFullScreen
                        style={{ height: chartHeight }}
                    />
                </Grid>
            </Grid>
        )
    }

    return (
        <SummaryIndicator
            title={i18n.resourceAndDemandManagementThresholds}
            bgColor='white'
            content={getContent()}
        />
    )
}

ManagementNeedsChart.propTypes = {
    iaeauResults: iaeauResultsShape,
    vpLevel: PropTypes.oneOf(Object.values(VP_LEVELS)),
    vpLevelValues: PropTypes.arrayOf(PropTypes.number),
    selectedStation: linkStationDetailsShape,
}

export default ManagementNeedsChart